import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import modelClassRequire from "@kaspernj/api-maker/build/model-class-require.js"

const modelClassData = {
  "attributes": {
    "attribute_name": {
      "column": {
        "default": null,
        "name": "attribute_name",
        "null": false,
        "type": "string"
      },
      "name": "attribute_name",
      "selected_by_default": null,
      "translated": null
    },
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "key": {
      "column": {
        "default": null,
        "name": "key",
        "null": false,
        "type": "string"
      },
      "name": "key",
      "selected_by_default": null,
      "translated": null
    },
    "resource_id": {
      "column": {
        "default": null,
        "name": "resource_id",
        "null": false,
        "type": null
      },
      "name": "resource_id",
      "selected_by_default": null,
      "translated": null
    },
    "resource_type": {
      "column": {
        "default": null,
        "name": "resource_type",
        "null": false,
        "type": "string"
      },
      "name": "resource_type",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "TextKey",
  "collectionKey": "text_keys",
  "collectionName": "text_keys",
  "i18nKey": "text_key",
  "camelizedLower": "textKey",
  "name": "TextKey",
  "nameDasherized": "text-key",
  "pluralName": "text_keys",
  "ransackable_associations": [
    {
      "className": "TextKey::Audit",
      "collectionName": "text_key_audits",
      "foreignKey": "text_key_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "TextKeyAudit",
      "through": null
    },
    {
      "className": "TextKey::Audit",
      "collectionName": "text_key_audits",
      "foreignKey": "text_key_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "TextKeyAudit",
      "through": null
    },
    {
      "className": "Resource",
      "collectionName": null,
      "foreignKey": "resource_id",
      "name": "resource",
      "macro": "belongs_to",
      "resource_name": null,
      "through": null
    },
    {
      "className": "TextValue",
      "collectionName": "text_values",
      "foreignKey": "text_key_id",
      "name": "text_values",
      "macro": "has_many",
      "resource_name": "TextValue",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "attribute_name",
      "column": {
        "default": null,
        "name": "attribute_name",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "key",
      "column": {
        "default": null,
        "name": "key",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "resource_id",
      "column": {
        "default": null,
        "name": "resource_id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "resource_type",
      "column": {
        "default": null,
        "name": "resource_type",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "with_missing_translation_in_locale"
    }
  ],
  "relationships": [
    {
      "className": "TextValue",
      "collectionName": "text_values",
      "foreignKey": "text_key_id",
      "name": "text_values",
      "macro": "has_many",
      "resource_name": "TextValue",
      "through": null
    }
  ],
  "paramKey": "text_key",
  "primaryKey": "id"
}

/** Frontend model for TextKey. */
class TextKey extends BaseModel {
  /** @returns {Record<string, any>} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  attributeName() {
    return this.readAttributeUnderscore("attribute_name")
  }

  /** @returns {boolean} */
  hasAttributeName() {
    const value = this.attributeName()

    return this._isPresent(value)
  }

  /** @returns {string} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  key() {
    return this.readAttributeUnderscore("key")
  }

  /** @returns {boolean} */
  hasKey() {
    const value = this.key()

    return this._isPresent(value)
  }

  /** @returns {any} */
  resourceId() {
    return this.readAttributeUnderscore("resource_id")
  }

  /** @returns {boolean} */
  hasResourceId() {
    const value = this.resourceId()

    return this._isPresent(value)
  }

  /** @returns {string} */
  resourceType() {
    return this.readAttributeUnderscore("resource_type")
  }

  /** @returns {boolean} */
  hasResourceType() {
    const value = this.resourceType()

    return this._isPresent(value)
  }

  /** @returns {string} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  static translate(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callCollectionCommand(
      {
        args,
        command: "translate",
        collectionName: "text_keys",
        type: "collection"
      },
      commandArgs
    ))
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./text-value.js").default>} */
  textValues() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const modelClass = modelClassRequire("TextValue")

    const ransack = {}

    ransack["text_key_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "text_values",
        model: this,
        modelName: "TextValue",
        modelClass
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./text-value.js").default>>} */
  loadTextValues() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const modelClass = modelClassRequire("TextValue")

    const ransack = {}

    ransack["text_key_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "text_values",
        model: this,
        modelClass
      },
      {ransack}
    )
  }
}

export default TextKey
